import React, { Fragment, useMemo } from 'react';
import { saveNote } from '../amlActions';
import { connect, ResolveThunks } from 'react-redux';
import AlertRowStatus from '../../alerts/AlertRowStatus';
import AlertRowAssignment from '../../alerts/AlertRowAssignment';
import { ApiSortDirection } from '../../api';
import Table from '../../table/Table';
import { MonitoringAlertHistory } from '../../alerts/AlertHistory';
import { Button, Dropdown } from 'react-bootstrap';
import NotesModal, { NotesModalFormValues } from '../NotesModal';
import { FormattedAlertNote, isNavigableCellClicked } from '../../common/helper';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import Has from '../../auth/Has';
import { CAN_ADD_NOTE, CAN_MODIFY_SNOOZE_CONFIGURATION } from '../../auth/permissions';
import { DropdownWrapper } from '../../common/DropdownWrapper';
import AlertDetailsPopover from '../../alerts/AlertDetailsPopover';
import TransactionDetailsPopover from '../../transaction/TransactionDetailsPopover';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { useLocation, useNavigate, Link } from 'react-router';
import SnoozeModal from '../../common/snooze/SnoozeModal';
import { InternalApiAlertType, InternalApiMonitoringAlert } from '../../api-internal';
import { useMe } from '../../organisation/useMe';
import { ContentPreviewer } from '../../common/ContentPreviewer';

interface TableAttribute {
  key: string;
  sortable: boolean;
}

interface AlertProps {
  personId: string;
  page: number;
  size: number;
  total: number;
  loading: boolean;
  alerts: Array<InternalApiMonitoringAlert>;
  attributes: TableAttribute[];
  onUpdate: () => void;
  onUpdatePage?: (page: number) => void;
  onUpdateFilter?: (
    filter: string | undefined,
    attribute?: string | undefined,
    direction?: ApiSortDirection | undefined
  ) => void;
  compact?: boolean;
  title?: string;
  expandable?: boolean;
  expandedByDefault?: boolean;
}

export const Alerts = ({
  personId,
  alerts,
  page,
  size,
  total,
  loading,
  onUpdate,
  onUpdatePage,
  onUpdateFilter,
  attributes,
  saveNote,
  compact = false,
  title = 'Monitoring alerts',
  expandable = false,
  expandedByDefault = true,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateToMonitoringAlert = (id: number) => navigate(`/monitoring/alerts/${id}`);
  const { features } = useMe();
  const monitoringAlertPageEnabled = isFeatureEnabled(ORGANISATION_FEATURE.MONITORING_ALERT_PAGE, features);

  const tableTitle = useMemo(
    () => (
      <>
        {title} {!loading && <span className='sanction-amount-info'>({total})</span>}
      </>
    ),
    [total, loading, title]
  );

  const updatePage = (page: number) => {
    onUpdatePage && onUpdatePage(page);
  };

  const sortByAttribute = (attribute: string, direction: ApiSortDirection, filter: string | undefined) => {
    onUpdateFilter && onUpdateFilter(filter, attribute, direction);
  };

  const onSubmitNote = async (values: NotesModalFormValues) => {
    saveNote(personId, values).then(() => {
      onUpdate();
    });
  };

  const navigateTo = (alert: InternalApiMonitoringAlert) => {
    // Do not do navigation if already on the page.
    if (monitoringAlertPageEnabled && location.pathname !== `/monitoring/alerts/${alert.id}`) {
      return navigateToMonitoringAlert(alert.id);
    }
  };

  return (
    <Table
      title={tableTitle}
      responsive={false}
      total={total}
      page={page}
      size={size}
      items={alerts}
      fetchPage={updatePage}
      onSearch={onUpdateFilter}
      attributes={attributes}
      onSort={sortByAttribute}
      fetching={loading}
      expandable={expandable}
      expandedByDefault={expandedByDefault}
      emptyMessage='No monitoring alerts'
      body={(alerts) =>
        alerts.map((alert: InternalApiMonitoringAlert, index) => (
          <Fragment key={alert.id}>
            <tr
              className={`custom-table-row-${index % 2} ${monitoringAlertPageEnabled && 'clickable-row'}`}
              onClick={(event: React.MouseEvent) => {
                isNavigableCellClicked(event) && navigateTo(alert);
              }}
            >
              <td>
                {monitoringAlertPageEnabled ? <Link to={`/monitoring/alerts/${alert.id}`}>{alert.id}</Link> : alert.id}
              </td>
              <td>
                <FormattedDateTime date={alert.createdTime} showTime />
              </td>
              <td>
                {alert.transaction && (
                  <>
                    <TransactionDetailsPopover transaction={alert.transaction} />
                    <span> • </span>
                  </>
                )}
                <AlertDetailsPopover alert={alert} withReason={true} />
              </td>
              {compact && (
                <Has allPermissions={CAN_MODIFY_SNOOZE_CONFIGURATION}>
                  <td>
                    <DropdownWrapper align='end'>
                      <Dropdown.Toggle as={Button} size='sm' className='btn-white' id='actions-dropdown'>
                        •••
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <SnoozeModal dropdownItem personId={personId} />
                      </Dropdown.Menu>
                    </DropdownWrapper>
                  </td>
                </Has>
              )}
              {!compact && (
                <>
                  <td>
                    <AlertRowAssignment alert={alert} />
                  </td>
                  <td>
                    <AlertRowStatus
                      alert={alert}
                      onChange={() => onUpdate()}
                      alertType={InternalApiAlertType.MONITORING}
                    />
                  </td>
                  <td>
                    <DropdownWrapper align='end'>
                      <Dropdown.Toggle as={Button} size='sm' className='btn-white' id='actions-dropdown'>
                        •••
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <MonitoringAlertHistory alert={alert} dropdownItem />
                        <Has allPermissions={CAN_ADD_NOTE}>
                          <NotesModal onSubmit={onSubmitNote} alert={alert} dropdownItem />
                        </Has>
                        <Has allPermissions={CAN_MODIFY_SNOOZE_CONFIGURATION}>
                          <SnoozeModal dropdownItem personId={personId} />
                        </Has>
                      </Dropdown.Menu>
                    </DropdownWrapper>
                  </td>
                </>
              )}
            </tr>
            {!compact &&
              alert.notes &&
              alert.notes.length > 0 &&
              alert.notes
                .slice()
                .sort((a, b) => {
                  if (a.createdTime && b.createdTime) {
                    return b.createdTime.getTime() - a.createdTime.getTime();
                  }
                  return 0;
                })
                .map((note, noteIndex) => (
                  <tr key={noteIndex} className={`custom-table-row-${index % 2}`}>
                    <td colSpan={6} className='alert-note-td'>
                      <div className='alert-note'>
                        <ContentPreviewer>
                          <FormattedAlertNote apiNote={note} />
                          {note.content}
                        </ContentPreviewer>
                      </div>
                    </td>
                  </tr>
                ))}
          </Fragment>
        ))
      }
    />
  );
};

const mapDispatchToProps = {
  saveNote,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & AlertProps;

export default connect(null, mapDispatchToProps)(Alerts);
